/**
 * Política de miniaturas del Explorer: evitar storm de decode en XA/cine.
 * CT/MR/US single-frame siguen pudiendo loadAndCacheImage vía cola limitada.
 * Thumbs de cine pesado: preferir previewUrl/webp HTTPS (nunca Part-10).
 */

import { resolveFileNumberOfFrames } from './seriesImageCounts'
import {
  isConstrainedUiDevice,
  isPhoneLikeDevice,
} from './mobileDownloadProfile'
import { isNonImageFileItem } from './isNonImageInstance'
import { buildSeriesMapForStudy } from './comparePriorStudy'

const HEAVY_CINE_MODALITIES = new Set(['XA', 'XRF', 'RF'])
/** SOP ≥ 8 MiB: tratar como cine/pesado aunque falte NumberOfFrames en stub. */
const HEAVY_BYTE_LENGTH = 8 * 1024 * 1024

/**
 * Stub RANGE_ZIP sin bytes: no pedir a Cornerstone (mismo guard que DicomViewer).
 * @param {string|null|undefined} imageId
 * @returns {boolean}
 */
export function isPendingExplorerImageId(imageId) {
  return /^udv-pending:/i.test(String(imageId || ''))
}

/**
 * URL liviana para miniatura (preview webp/jpg), si existe.
 * @param {object|null|undefined} file
 * @returns {string|null}
 */
export function resolveExplorerThumbnailPreviewUrl(file) {
  if (!file) return null
  const candidates = [
    file.previewUrl,
    file.renderKind === 'preview' ? file.imageId : null,
    file.webpKey,
  ]
  for (let i = 0; i < candidates.length; i++) {
    const raw = candidates[i]
    if (raw == null) continue
    const s = String(raw).trim()
    if (!s) continue
    if (/^https?:\/\//i.test(s)) return s
  }
  return null
}

/**
 * Serie/SOP donde Explorer no debe disparar loadAndCacheImage DICOM propio.
 * Espera `file.image` del scheduler (1ª de serie con keepPixels) o usa preview URL.
 * @param {object|null|undefined} file
 * @returns {boolean}
 */
export function isHeavyCineThumbnailFile(file) {
  if (!file) return false
  const mod = String(
    (file.series && (file.series.modality || file.series.Modality)) ||
      file.modality ||
      file.Modality ||
      ''
  )
    .trim()
    .toUpperCase()
  if (HEAVY_CINE_MODALITIES.has(mod)) return true

  const nf = resolveFileNumberOfFrames(file)
  if (nf > 1) return true

  const bytes =
    Number(file.byteLength) ||
    Number(file.bytes) ||
    Number(file.size) ||
    Number(file.contentLength) ||
    0
  return Number.isFinite(bytes) && bytes >= HEAVY_BYTE_LENGTH
}

/**
 * Pareo ordinal no-imagen ↔ serie con píxeles (p.ej. 5 PR + 5 XA hemodinamia).
 * Cuando hay la misma cantidad, el i-ésimo PR se asocia al i-ésimo displayable.
 *
 * @param {Array<{ index: number, nonImage: boolean }>} slots
 * @param {number} nonImageExplorerIndex
 * @returns {number|null} índice explorer de la serie imagen pareada
 */
export function findOrdinalPairedDisplayableIndex(slots, nonImageExplorerIndex) {
  const list = Array.isArray(slots) ? slots : []
  const nonImageIdxs = []
  const displayIdxs = []
  for (let i = 0; i < list.length; i++) {
    const s = list[i]
    if (!s || typeof s.index !== 'number') continue
    if (s.nonImage) nonImageIdxs.push(s.index)
    else displayIdxs.push(s.index)
  }
  if (!nonImageIdxs.length || nonImageIdxs.length !== displayIdxs.length) {
    return null
  }
  const ord = nonImageIdxs.indexOf(nonImageExplorerIndex)
  if (ord < 0) return null
  const paired = displayIdxs[ord]
  return typeof paired === 'number' ? paired : null
}

/**
 * Construye slots de clasificación para findOrdinalPairedDisplayableIndex.
 * @param {Array<*>} seriesKeys
 * @param {(key: *) => object|null|undefined} getFirstFile
 * @returns {Array<{ index: number, nonImage: boolean }>}
 */
export function buildExplorerNonImageSlots(seriesKeys, getFirstFile) {
  const keys = Array.isArray(seriesKeys) ? seriesKeys : []
  const out = []
  for (let i = 0; i < keys.length; i++) {
    const first =
      typeof getFirstFile === 'function' ? getFirstFile(keys[i]) : null
    if (!first) continue
    out.push({ index: i, nonImage: isNonImageFileItem(first) })
  }
  return out
}

/**
 * Concurrencia máxima de generateThumbnail (decode + renderToCanvas).
 * @returns {number}
 */
export function getExplorerThumbnailMaxConcurrent() {
  if (isPhoneLikeDevice() || isConstrainedUiDevice()) return 1
  return 2
}

/**
 * Archivos de una serie: mapa bound o búsqueda en studyList (merge historial).
 * @param {*} seriesKey
 * @param {Map|null|undefined} seriesList
 * @param {Map|null|undefined} studyList
 * @returns {object[]}
 */
export function resolveExplorerSeriesFiles(seriesKey, seriesList, studyList) {
  if (seriesKey == null) return []
  if (seriesList && typeof seriesList.has === 'function' && seriesList.has(seriesKey)) {
    return seriesList.get(seriesKey) || []
  }
  if (!studyList || typeof studyList.values !== 'function') return []
  const studies = studyList.values()
  for (const studyFiles of studies) {
    const { seriesList: sl } = buildSeriesMapForStudy(studyFiles || [])
    if (sl && sl.has(seriesKey)) return sl.get(seriesKey) || []
  }
  return []
}

/**
 * Series visibles para miniaturas: todos los estudios no colapsados.
 * Un solo estudio → bound seriesKeys.
 *
 * @param {{
 *   studyKeys?: *[],
 *   collapsedStudyKeys?: *[],
 *   studyList?: Map,
 *   boundSeriesKeys?: *[],
 *   boundSeriesList?: Map,
 * }} opts
 * @returns {Array<{ seriesKey: *, files: object[] }>}
 */
export function listVisibleExplorerThumbnailSeries(opts) {
  const o = opts || {}
  const studyKeys = Array.isArray(o.studyKeys) ? o.studyKeys : []
  const collapsed = Array.isArray(o.collapsedStudyKeys) ? o.collapsedStudyKeys : []
  const collapsedSet = new Set(collapsed.map((k) => String(k)))
  const studyList = o.studyList

  if (studyKeys.length > 1 && studyList && typeof studyList.get === 'function') {
    const out = []
    for (let i = 0; i < studyKeys.length; i++) {
      const studyKey = studyKeys[i]
      if (studyKey == null) continue
      if (collapsedSet.has(String(studyKey))) continue
      const studyFiles = studyList.has(studyKey)
        ? studyList.get(studyKey) || []
        : []
      const { seriesList, seriesKeys } = buildSeriesMapForStudy(studyFiles)
      for (let j = 0; j < seriesKeys.length; j++) {
        const seriesKey = seriesKeys[j]
        out.push({
          seriesKey,
          files: seriesList.get(seriesKey) || [],
        })
      }
    }
    return out
  }

  const boundKeys = Array.isArray(o.boundSeriesKeys) ? o.boundSeriesKeys : []
  const boundList = o.boundSeriesList
  return boundKeys.map((seriesKey) => ({
    seriesKey,
    files:
      boundList && typeof boundList.get === 'function'
        ? boundList.get(seriesKey) || []
        : [],
  }))
}
